import { t } from "@/i18n-js/instance";
import { ApiError } from "@circle-react/config/CustomErrors";
import { safeJsonParse } from "./safeJsonParse";

export interface ErrorJsonBody {
  failure?: {
    message: string;
  };
  view_only_masquerading?: boolean;
  message?: string;
  error_details?: string;
}

export interface JsonFetchOptions extends RequestInit {
  headers?: Record<string, string>;
}

export async function jsonFetchRequest<T = any>(
  url: string,
  options?: JsonFetchOptions,
) {
  const response = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...options?.headers,
    },
    credentials: "same-origin",
  });

  if (response.ok) {
    try {
      const text = await response.text();
      if (text.length === 0) return undefined as T;
      return JSON.parse(text) as T;
    } catch (error: unknown) {
      const details = error instanceof Error ? error.message : "";
      throw new ApiError({
        message: `Failed to parse response`,
        status: response.status,
        statusText: response.statusText,
        errorDetails: details,
      });
    }
  } else {
    return await throwApiError(response);
  }
}

export const throwApiError = async (response: Response) => {
  let json: ErrorJsonBody;

  try {
    const text = await response.text();
    json = text.length > 0 ? (JSON.parse(text) as ErrorJsonBody) : {};
  } catch {
    json = { message: `Failed to parse error response` };
  }

  const { status, statusText } = response;

  if (json?.view_only_masquerading) {
    throw new ApiError({
      message: t("view_only_error"),
      status,
      statusText,
      errorDetails: t("view_only_error"),
      body: json,
    });
  }

  const message =
    json?.message || json?.failure?.message || "Failed to retrieve data!";

  const { error_details = json?.message } = json;

  throw new ApiError({
    message,
    status,
    statusText,
    errorDetails: error_details,
    body: json,
  });
};

export const reactQueryGet = <T = any>(url: string) =>
  jsonFetchRequest<T>(url, { method: "GET" });

export const reactQueryPut = <T = any>(url: string, bodyParams?: object) =>
  jsonFetchRequest<T>(url, {
    method: "PUT",
    body: JSON.stringify(bodyParams),
  });

export const reactQueryPatch = <T = any>(url: string, bodyParams?: object) =>
  jsonFetchRequest<T>(url, {
    method: "PATCH",
    body: JSON.stringify(bodyParams),
  });

export const reactQueryPost = <T = any>(url: string, bodyParams?: object) =>
  jsonFetchRequest<T>(url, {
    method: "POST",
    body: JSON.stringify(bodyParams),
  });

export const reactQueryDelete = <T = any>(url: string, bodyParams?: object) => {
  const options: JsonFetchOptions = {
    method: "DELETE",
  };

  if (bodyParams) {
    options.body = JSON.stringify(bodyParams);
  }

  return jsonFetchRequest<T>(url, options);
};

/**
 * @deprecated Please use jsonFetchRequest instead
 */
export interface FetchOptions extends RequestInit {
  headers?: Record<string, string>;
  forReactQuery?: boolean;
}

/**
 * @deprecated Please use jsonFetchRequest instead
 */
export type OptionsType = FetchOptions | undefined;

/**
 * @deprecated Please use jsonFetchRequest instead
 */
export const serializeJsonResponse = async <T = unknown>(
  response: Response,
): Promise<T | undefined | Record<string, never>> => {
  if (response.ok) {
    const text = await response.text();
    if (text.length > 0) {
      const parsed = safeJsonParse<T>(text);
      return parsed || {};
    }
    return {};
  }

  return throwApiError(response);
};

/**
 * @deprecated Please use jsonFetchRequest from backendRequestHelpers.ts instead, or write your own fetch() call
 */
export async function fetchRequest<T = any>(
  url: string,
  options: OptionsType = {},
): Promise<Response | T | Record<string, never> | undefined> {
  const {
    headers = {},
    forReactQuery: isForReactQuery,
    ...passThroughOptions
  } = options;

  const response = await fetch(url, {
    ...passThroughOptions,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...headers,
    },
    credentials: "same-origin",
  });

  if (!isForReactQuery) {
    return response;
  }

  return serializeJsonResponse<T>(response);
}
